import { initiateEmailOtp, mapCognitoError, respondEmailOtp } from "@repo/auth";
import { Logo } from "@repo/ui/components/logo";
import { OtpSignInForm } from "@repo/ui/components/otp-sign-in-form";
import { SignInForm } from "@repo/ui/components/sign-in-form";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { PortalAccessDeniedError, restoreProfile } from "@/shared/edge/client";
import { EDGE_URL, session } from "@/shared/edge/session";
export const Route = createFileRoute("/login")({
  validateSearch: z.object({ redirect: z.string().optional().catch(""), error: z.string().optional().catch("") }),
  beforeLoad: ({ search }) => {
    if (session.getProfile()) throw redirect({ to: safeRedirect(search.redirect) });
  },
  component: LoginComponent,
});
function safeRedirect(path?: string) {
  return path?.startsWith("/") && !path.startsWith("//") ? path : "/";
}
function LoginComponent() {
  const router = useRouter();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const [stage, setStage] = useState<"email" | "code">("email");
  const [email, setEmail] = useState<string>();
  const [challenge, setChallenge] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(
    search.error === "access-denied" ? new PortalAccessDeniedError().message : undefined,
  );
  const config = { region: import.meta.env.VITE_COGNITO_REGION, clientId: import.meta.env.VITE_COGNITO_CLIENT_ID };
  async function requestCode(input: string) {
    setLoading(true);
    setError(undefined);
    try {
      const result = await initiateEmailOtp({ ...config, email: input });
      setChallenge(result.session);
      setEmail(input);
      setStage("code");
    } catch (error) {
      setError(error instanceof PortalAccessDeniedError ? error.message : mapCognitoError(error));
    } finally {
      setLoading(false);
    }
  }
  async function submitCode(code: string) {
    if (!email || !challenge) return;
    setLoading(true);
    setError(undefined);
    try {
      const { idToken } = await respondEmailOtp({ ...config, email, session: challenge, code });
      const response = await fetch(`${EDGE_URL}/auth/otp/exchange`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      if (!response.ok) throw new Error("We couldn't complete sign-in. Please try again.");
      session.accept(await response.json());
      await restoreProfile();
      await router.invalidate();
      await navigate({ to: safeRedirect(search.redirect) });
    } catch (error) {
      setError(error instanceof PortalAccessDeniedError ? error.message : mapCognitoError(error));
    } finally {
      setLoading(false);
    }
  }
  if (import.meta.env.VITE_AUTH_MODE === "password" && import.meta.env.DEV)
    return (
      <div className="flex min-h-svh w-full items-center justify-center bg-background">
        <SignInForm
          loading={loading}
          error={error}
          onSubmit={async (credentials) => {
            setLoading(true);
            setError(undefined);
            try {
              const response = await fetch(`${EDGE_URL}/auth/password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(credentials),
              });
              if (!response.ok) throw new Error("Could not sign in with this portal account.");
              session.accept(await response.json());
              await restoreProfile();
              await router.invalidate();
              await navigate({ to: safeRedirect(search.redirect) });
            } catch (error) {
              setError(error instanceof PortalAccessDeniedError ? error.message : mapCognitoError(error));
            } finally {
              setLoading(false);
            }
          }}
        >
          <Logo variant="adaptive" className="h-8" title={null} />
          <h1>Patient Portal</h1>
        </SignInForm>
      </div>
    );
  return (
    <div className="flex min-h-svh w-full items-center justify-center bg-background">
      <OtpSignInForm
        stage={stage}
        email={email}
        onRequestCode={requestCode}
        onSubmitCode={submitCode}
        onBack={() => {
          setStage("email");
          setChallenge(undefined);
          setError(undefined);
        }}
        loading={loading}
        error={error}
      >
        <Logo variant="adaptive" className="h-8" title={null} />
        <h1 className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
          <span className="sr-only">MindForge </span>Patient Portal
        </h1>
      </OtpSignInForm>
    </div>
  );
}
